import React, { useEffect } from "react";
import { Modal, Drawer, Form, Input, Select, Switch, Row, Col, Button, Space, message } from "antd";
import { useCreate, useUpdate } from "../../../../hooks";
import { contractManagementService } from "../../../../api/enhancedApi";
import { SERVICE_TYPE_OPTIONS } from "../../SupplierContract/constants";

const { TextArea } = Input;

const ProductBundleForm = ({ 
    visible, 
    mode, 
    initialValues, 
    onSuccess, 
    onCancel, 
    useDrawer = false,  // 新增：是否使用抽屉模式
    width = 900 
}) => {
    const [form] = Form.useForm();

    // 创建和更新的hooks
    const createMutation = useCreate("productBundles", contractManagementService.productBundle.createProductBundle, {
        onSuccess: () => {
            message.success("创建成功");
            onSuccess();
        },
        onError: (error) => {
            console.error("创建失败:", error);
            const errorMessage = error?.response?.data?.detail || error?.message || "创建失败";
            message.error(errorMessage);
        },
    });

    const updateMutation = useUpdate("productBundles", contractManagementService.productBundle.updateProductBundle, {
        onSuccess: () => {
            message.success("更新成功");
            onSuccess();
        },
        onError: (error) => {
            console.error("更新失败:", error);
            const errorMessage = error?.response?.data?.detail || error?.message || "更新失败";
            message.error(errorMessage);
        },
    });

    // 当弹窗打开时，设置表单初始值
    useEffect(() => {
        if (visible) {
            if (mode === "edit" && initialValues) {
                console.log("🔧 ProductBundleForm - 编辑模式，初始数据:", initialValues);
                console.log("🔧 ProductBundleForm - mode:", mode, "visible:", visible);
                
                // 编辑模式：设置表单值
                const formValues = {
                    ...initialValues,
                    // 确保数组字段是正确格式
                    applicable_airports: Array.isArray(initialValues.applicable_airports) 
                        ? initialValues.applicable_airports 
                        : (initialValues.applicable_airports ? [initialValues.applicable_airports] : []),
                    applicable_aircraft_types: Array.isArray(initialValues.applicable_aircraft_types) 
                        ? initialValues.applicable_aircraft_types 
                        : (initialValues.applicable_aircraft_types ? [initialValues.applicable_aircraft_types] : []),
                };
                console.log("🔧 ProductBundleForm - 处理后的表单数据:", formValues);
                
                // 使用setTimeout确保表单完全渲染后再设置值
                setTimeout(() => {
                    console.log("🔧 ProductBundleForm - 延迟设置表单值");
                    form.setFieldsValue(formValues);
                    console.log("🔧 ProductBundleForm - 表单值设置完成，当前表单值:", form.getFieldsValue());
                }, 0);
            } else {
                console.log("🔧 ProductBundleForm - 新增模式，mode:", mode, "visible:", visible);
                // 新增模式：重置表单并设置默认值
                form.resetFields();
                setTimeout(() => {
                    form.setFieldsValue({
                        is_active: true,
                        service_type: "CATERING",
                        applicable_airports: [],
                        applicable_aircraft_types: [],
                    });
                }, 0);
            }
        }
    }, [visible, mode, initialValues, form]);

    // 处理提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // 处理数组字段，确保是数组格式
            const processedValues = {
                ...values,
                applicable_airports: Array.isArray(values.applicable_airports) 
                    ? values.applicable_airports 
                    : [],
                applicable_aircraft_types: Array.isArray(values.applicable_aircraft_types) 
                    ? values.applicable_aircraft_types 
                    : [],
            };

            // 清理空字符串和空数组
            Object.keys(processedValues).forEach(key => {
                if (processedValues[key] === "" || processedValues[key] === null) {
                    processedValues[key] = undefined;
                }
                if (Array.isArray(processedValues[key]) && processedValues[key].length === 0) {
                    processedValues[key] = [];
                }
            });

            if (mode === "add") {
                // 创建时，移除undefined字段
                const createData = Object.fromEntries(
                    Object.entries(processedValues).filter(([_, value]) => value !== undefined)
                );
                createMutation.mutate(createData);
            } else {
                // 更新时保留所有字段
                updateMutation.mutate({ id: initialValues.id, data: processedValues });
            }
        } catch (error) {
            console.error("表单验证失败:", error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        console.log("🔧 ProductBundleForm - 取消操作，重置表单");
        form.resetFields();
        onCancel();
    };

    const isLoading = createMutation.isLoading || updateMutation.isLoading;
    const title = mode === "add" ? "新增产品套装" : "编辑产品套装";

    // 准备表单初始值
    const getFormInitialValues = () => {
        if (mode === "edit" && initialValues) {
            return {
                ...initialValues,
                // 确保数组字段是正确格式
                applicable_airports: Array.isArray(initialValues.applicable_airports) 
                    ? initialValues.applicable_airports 
                    : (initialValues.applicable_airports ? [initialValues.applicable_airports] : []),
                applicable_aircraft_types: Array.isArray(initialValues.applicable_aircraft_types) 
                    ? initialValues.applicable_aircraft_types 
                    : (initialValues.applicable_aircraft_types ? [initialValues.applicable_aircraft_types] : []),
            };
        }
        return {
            is_active: true,
            service_type: "CATERING",
            applicable_airports: [],
            applicable_aircraft_types: [],
        };
    };

    // 表单内容
    const formContent = (
        <Form 
            form={form} 
            layout="vertical" 
            preserve={false}
            key={`${mode}-${visible}-${initialValues?.id || 'new'}`}
            initialValues={getFormInitialValues()}
        >
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="name"
                        label="产品套装名称"
                        rules={[
                            { required: true, message: "请输入产品套装名称" },
                            { max: 200, message: "产品套装名称不能超过200个字符" },
                            { min: 2, message: "产品套装名称至少2个字符" },
                        ]}
                    >
                        <Input placeholder="请输入产品套装名称" />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item 
                        name="bundle_code" 
                        label="产品套装编码"
                        rules={[
                            { max: 100, message: "产品套装编码不能超过100个字符" },
                            { pattern: /^[A-Za-z0-9_-]*$/, message: "产品套装编码只能包含字母、数字、下划线和连字符" }
                        ]}
                    >
                        <Input placeholder="请输入产品套装编码（可选）" />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item 
                        name="service_type" 
                        label="服务类型" 
                        rules={[{ required: true, message: "请选择服务类型" }]}
                    >
                        <Select placeholder="请选择服务类型" options={SERVICE_TYPE_OPTIONS} />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item name="is_active" label="状态" valuePropName="checked">
                        <Switch checkedChildren="启用" unCheckedChildren="停用" />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item 
                name="description" 
                label="产品套装描述"
                rules={[{ max: 1000, message: "产品套装描述不能超过1000个字符" }]}
            >
                <TextArea 
                    rows={3} 
                    placeholder="请输入产品套装描述" 
                    showCount 
                    maxLength={1000}
                />
            </Form.Item>

            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item 
                        name="applicable_airports" 
                        label="适用机场" 
                        help="输入机场代码后按回车添加，可添加多个机场。留空表示适用于所有机场"
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value && value.length > 0) {
                                        // 验证机场代码格式（3个字母）
                                        const invalidCodes = value.filter(code => !/^[A-Z]{3}$/.test(code));
                                        if (invalidCodes.length > 0) {
                                            return Promise.reject(new Error(`机场代码格式错误: ${invalidCodes.join(', ')}。应为3个大写字母（如：PEK、SHA）`));
                                        }
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Select 
                            mode="tags" 
                            placeholder="请输入机场代码（如：PEK、SHA、CAN）" 
                            tokenSeparators={[",", " "]} 
                            style={{ width: "100%" }}
                            onInputKeyDown={(e) => {
                                // 自动转换为大写
                                if (e.target.value) {
                                    e.target.value = e.target.value.toUpperCase();
                                }
                            }}
                        />
                    </Form.Item>
                </Col>

                <Col span={12}>
                    <Form.Item 
                        name="applicable_aircraft_types" 
                        label="适用机型" 
                        help="输入机型代码后按回车添加，可添加多个机型。留空表示适用于所有机型"
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (value && value.length > 0) {
                                        // 验证机型代码格式
                                        const invalidCodes = value.filter(code => !/^[A-Z0-9]{2,10}$/.test(code));
                                        if (invalidCodes.length > 0) {
                                            return Promise.reject(new Error(`机型代码格式错误: ${invalidCodes.join(', ')}。应为2-10位字母数字组合（如：A320、B737）`));
                                        }
                                    }
                                    return Promise.resolve();
                                }
                            }
                        ]}
                    >
                        <Select 
                            mode="tags" 
                            placeholder="请输入机型代码（如：A320、B737、A350）" 
                            tokenSeparators={[",", " "]} 
                            style={{ width: "100%" }}
                            onInputKeyDown={(e) => {
                                // 自动转换为大写
                                if (e.target.value) {
                                    e.target.value = e.target.value.toUpperCase();
                                }
                            }}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Form.Item 
                name="remarks" 
                label="备注"
                rules={[{ max: 500, message: "备注不能超过500个字符" }]}
            >
                <TextArea 
                    rows={2} 
                    placeholder="请输入备注信息" 
                    showCount 
                    maxLength={500}
                />
            </Form.Item>
        </Form>
    );

    // 根据useDrawer决定使用Modal还是Drawer
    if (useDrawer) {
        return (
            <Drawer
                title={title}
                placement="right"
                onClose={handleCancel}
                open={visible}
                width={width}
                destroyOnHidden
                maskClosable={false}
                extra={
                    <Space>
                        <Button 
                            size="small"
                            onClick={handleCancel}
                            disabled={isLoading}
                        >
                            取消
                        </Button>
                        <Button 
                            type="primary"
                            size="small"
                            onClick={handleSubmit}
                            loading={isLoading}
                        >
                            保存
                        </Button>
                    </Space>
                }
            >
                {formContent}
            </Drawer>
        );
    }

    return (
        <Modal 
            title={title}
            open={visible} 
            onOk={handleSubmit} 
            onCancel={handleCancel} 
            confirmLoading={isLoading} 
            width={width} 
            destroyOnHidden
            maskClosable={false}
            okText="保存"
            cancelText="取消"
        >
            {formContent}
        </Modal>
    );
};

export default ProductBundleForm;
